/**
 * Lightweight cache for Planscreen filters across pages.
 * Cached by communityId + contractId (no endpoint).
 */

const STORAGE_KEY = "planscreen_filters_cache_v2";
const DEFAULT_TTL_MS = 30 * 60 * 1000; // 30 minutes

const readCache = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

const writeCache = (cache) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch (e) {
    // Ignore write failures (storage full/disabled)
  }
};

export const getCommunityContractKey = (communityId, contractId) => {
  const safeCommunityId = communityId ?? "0";
  const safeContractId = contractId ?? "0";
  return `${safeCommunityId}|${safeContractId}`;
};

const getCommunityEntry = (communityId, maxAgeMs = DEFAULT_TTL_MS) => {
  if (!communityId) return null;
  const cache = readCache();
  const entry = cache[String(communityId)];
  if (!entry || typeof entry !== "object") return null;

  const age = Date.now() - (entry.timestamp || 0);
  if (age > maxAgeMs) {
    delete cache[String(communityId)];
    writeCache(cache);
    return null;
  }

  return entry;
};

export const getCachedFiltersForContract = (
  communityId,
  contractId,
  maxAgeMs = DEFAULT_TTL_MS
) => {
  const entry = getCommunityEntry(communityId, maxAgeMs);
  if (!entry || !entry.contracts) return null;
  return entry.contracts[String(contractId)] ?? null;
};

export const setCachedFiltersForContract = (
  communityId,
  contractId,
  data
) => {
  if (!communityId || !contractId || !Array.isArray(data)) return;
  const cache = readCache();
  const key = String(communityId);
  const existing = cache[key] && typeof cache[key] === "object" ? cache[key] : {};
  const contracts =
    existing.contracts && typeof existing.contracts === "object"
      ? existing.contracts
      : {};

  cache[key] = {
    timestamp: Date.now(),
    contracts: {
      ...contracts,
      [String(contractId)]: data,
    },
  };

  writeCache(cache);
};
